import { DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';

export interface Event {
    account_id: string;
    type: EventType;
    level: EventLevel;
    category: EventCategory;
    data: string | null;
    creation_timestamp: string;
    /** User id of the event actor. */
    actor?: string | null;
    /** SPOT id of the event location. */
    location?: string | null;
    user_id: string | null;
    spot_id: string | null;
    module_id: string | null;
    slot_id: string | null;
    iot_id: string | null;
    device_id: string | null;
    locker_id: string | null;
    product_id: string | null;
    equipment_id: string | null;
    problem_id: string | null;
    transaction_id: string | null;
    package_id: string | null;
    colli_id: string | null;
    user_interface_id: string | null;
    workflow_id: string | null;
    identification_id: string | null;
    id: string;
}

export enum EventEntityType {
    ACCOUNT = 'account',
    ANSWER = 'answer',
    ANY = 'any',
    ATTRIBUTE = 'attribute',
    ATTRIBUTE_VALUE = 'attribute_value',
    AUDIT_LOG = 'audit_log',
    BASKET = 'basket',
    BASKET_ITEM = 'basket_item',
    BUDGET = 'budget',
    BUDGET_ENTITY = 'budget_entity',
    BUDGET_RULE = 'budget_rule',
    BUDGET_USE = 'budget_use',
    CERTIFICATE = 'certificate',
    CERTIFICATE_REQUIREMENT = 'certificate_requirement',
    COLLI = 'colli',
    COLLI_CONTENT = 'colli_content',
    COLLI_TIMER = 'colli_timer',
    CONSTRAINT = 'constraint',
    COST_CENTER = 'cost_center',
    COST_CENTER_ENTITY = 'cost_center_entity',
    CUSTOM_ROLE = 'custom_role',
    DEVICE = 'device',
    DOCKER_IMAGE = 'docker_image',
    DOCUMENT = 'document',
    EQUIPMENT = 'equipment',
    EQUIPMENT_CERTIFICATE = 'equipment_certificate',
    EQUIPMENT_COUNT = 'equipment_count',
    EQUIPMENT_GROUP = 'equipment_group',
    EQUIPMENT_PROBLEM = 'equipment_problem',
    EQUIPMENT_TIMER = 'equipment_timer',
    EQUIPMENT_USE = 'equipment_use',
    EQUIPMENT_VARIABLE = 'equipment_variable',
    EVENT = 'event',
    EXTERNAL_ENTITY_MAPPING = 'external_entity_mapping',
    FUNCTIONALITY = 'functionality',
    GROUP = 'group',
    GROUP_MEMBER = 'group_member',
    IDENTIFICATION = 'identification',
    INBOX_MESSAGE = 'inbox_message',
    INVITE = 'invite',
    IOT = 'iot',
    IOT_TROUBLESHOOT = 'iot_troubleshoot',
    LABEL = 'label',
    LABEL_CONTENT = 'label_content',
    LABEL_TEMPLATE = 'label_template',
    LICENSE = 'license',
    LOCKER = 'locker',
    MODULE = 'module',
    MODULE_TEMPLATE = 'module_template',
    NOTIFICATION_CONTENT = 'notification_content',
    NOTIFICATION_EMAIL = 'notification_email',
    NOTIFICATION_TEMPLATE = 'notification_template',
    ORDER = 'order',
    ORDER_ITEM = 'order_item',
    OUTBOX_MESSAGE = 'outbox_message',
    PACKAGE = 'package',
    PACKAGE_TIMER = 'package_timer',
    PAYMENT = 'payment',
    PIN_CODE = 'pin_code',
    PLAYLIST = 'playlist',
    PLAYLIST_ITEM = 'playlist_item',
    PROBLEM = 'problem',
    PROBLEM_ENTITY = 'problem_entity',
    PRODUCT = 'product',
    PRODUCT_ATTRIBUTE = 'product_attribute',
    PRODUCT_CATEGORY = 'product_category',
    PRODUCT_GROUP = 'product_group',
    QUESTION = 'question',
    QUESTIONNAIRE = 'questionnaire',
    REPORT = 'report',
    REPORT_JOB = 'report_job',
    REPORT_SCHEDULE = 'report_schedule',
    ROLE = 'role',
    SETTING = 'setting',
    SLOT = 'slot',
    SLOT_TEMPLATE = 'slot_template',
    SPOT = 'spot',
    SSO_PROVIDER = 'sso_provider',
    STOCK = 'stock',
    STOCK_MOVEMENT = 'stock_movement',
    STOCK_REQUIREMENT = 'stock_requirement',
    SUPPLIER = 'supplier',
    TEST_RUN = 'test_run',
    TRANSACTION = 'transaction',
    TRANSACTION_COUNT = 'transaction_count',
    TRANSACTION_TIMER = 'transaction_timer',
    TRANSACTION_ACTOR = 'transaction_actor',
    TRANSACTION_USER = 'transaction_user',
    USER = 'user',
    USER_CERTIFICATE = 'user_certificate',
    USER_GROUP = 'user_group',
    USER_INTERFACE = 'user_interface',
    USER_INTERFACE_BUTTON = 'user_interface_button',
    WIZARD = 'wizard',
    WIZARD_STEP = 'wizard_step',
    WORKFLOW = 'workflow',
    WORKFLOW_FIELD = 'workflow_field',
    WORKFLOW_STEP = 'workflow_step',

    // Not known by backend
    ADDRESS = 'address',
    ADDRESS_ENTITY = 'address_entity',
    ATTRIBUTE_LINKING = 'attribute_linking',
    AUTHENTICATION = 'authentication',
    CATEGORY = 'category',
    DOCUMENT_ENTITY = 'document_entity',
    EQUIPMENT_STATE = 'equipment_state',
    IOT_UNKNOWN = 'iot_unknown',
    MEDIA_FILE = 'media_file',
    PRODUCT_OWNER = 'product_owner',
    RULE = 'rule',
    SLOT_CONSTRAINT = 'slot_constraint',
    SLOT_TWIN = 'slot_twin',
    USE = 'use',
    WORKFLOW_VARIABLE = 'workflow_variable'
}

export enum EventLevel {
    ALERT = 'alert',
    DEBUG = 'debug',
    ERROR = 'error',
    INFO = 'info',
    WARNING = 'warning'
}

export enum EventType {
    COLLI_ACTION_TRIGGERED = 'colli_action_triggered',
    COLLI_CODE_SCANNED = 'colli_code_scanned',
    COLLI_HISTORY_LOGGED = 'colli_history_logged',
    COLLI_STATE_CHANGED = 'colli_state_changed',
    COLLI_TRIGGER_RECEIVED = 'colli_trigger_received',
    COURIER_NOTIFICATION_RECEIVED = 'courier_notification_received',
    DEVICE_STATUS_UPDATED = 'device_status_updated',
    EQUIPMENT_ACTION_TRIGGERED = 'equipment_action_triggered',
    EQUIPMENT_CODE_SCANNED = 'equipment_code_scanned',
    EQUIPMENT_STATE_CHANGED = 'equipment_state_changed',
    EQUIPMENT_TRIGGER_RECEIVED = 'equipment_trigger_received',
    IDENTIFICATION_CREATED = 'identification_created',
    IDENTIFICATION_EXPIRED = 'identification_expired',
    IDENTIFICATION_FAILED = 'identification_failed',
    IDENTIFICATION_REMOVED = 'identification_removed',
    IDENTIFICATION_UPDATED = 'identification_updated',
    IOT_ACCESS_KEY_SCANNED = 'iot_access_key_scanned',
    IOT_ACTION_TRIGGERED = 'iot_action_triggered',
    IOT_HEARTBEAT_MISSED = 'iot_heartbeat_missed',
    IOT_HEARTBEAT_RECOVERED = 'iot_heartbeat_recovered',
    IOT_STATE_CHANGED = 'iot_state_changed',
    IOT_TRIGGER_RECEIVED = 'iot_trigger_received',
    ISSUE_REPORTED = 'issue_reported',
    KEYBOARD_INPUT_ENTERED = 'keyboard_input_entered',
    KEYPAD_INPUT_ENTERED = 'keypad_input_entered',
    LOCKER_STATUS_UPDATED = 'locker_status_updated',
    NFC_BADGE_SCANNED = 'nfc_badge_scanned',
    NFC_TAG_SCANNED = 'nfc_tag_scanned',
    NOTIFICATION_FAILED = 'notification_failed',
    NOTIFICATION_SENT = 'notification_sent',
    PACKAGE_ACTION_TRIGGERED = 'package_action_triggered',
    PACKAGE_STATE_CHANGED = 'package_state_changed',
    PACKAGE_TRIGGER_RECEIVED = 'package_trigger_received',
    PERSONNEL_BADGE_SCANNED = 'personnel_badge_scanned',
    PIN_CODE_EXPIRED = 'pin_code_expired',
    PIN_CODE_SCANNED = 'pin_code_scanned',
    QR_CODE_SCANNED = 'qr_code_scanned',
    RAKINDA_ACCESS_DENIED = 'rakinda_access_denied',
    RAKINDA_ACCESS_GRANTED = 'rakinda_access_granted',
    RAKINDA_MASTER_PIN_CODE_USED = 'rakinda_master_pin_code_used',
    TRANSACTION_ACTION_TRIGGERED = 'transaction_action_triggered',
    TRANSACTION_CODE_SCANNED = 'transaction_code_scanned',
    TRANSACTION_HISTORY_LOGGED = 'transaction_history_logged',
    TRANSACTION_CURRENT_STATE_CHANGED = 'transaction_current_state_changed',
    TRANSACTION_STATE_CHANGED = 'transaction_state_changed',
    TRANSACTION_TRIGGER_RECEIVED = 'transaction_trigger_received',
    USER_ACCESS_KEY_SCANNED = 'user_access_key_scanned',
    USER_ACTION_TRIGGERED = 'user_action_triggered',
    USER_HISTORY_LOGGED = 'user_history_logged',
    USER_STATE_CHANGED = 'user_state_changed',
    USER_TRIGGER_RECEIVED = 'user_trigger_received',

    // Not known by backend
    DEVICE_LOG_RECEIVED = 'device_log_received',
    EMAIL_SENT = 'email_sent'
}

export enum EventCategory {
    ACCESS_CONTROL = 'access_control',
    COURIER = 'courier',
    IDENTIFICATION = 'identification',
    IOT = 'iot',
    NOTIFICATION = 'notification',
    WORKFLOW = 'workflow',

    // Not known by backend
    DATA_IMPORT = 'data_import',
    SYNC = 'sync',
    WEBSOCKET = 'websocket'
}

export interface EventEntity {
    id: string;
    event_id: string;
    entity_type: EventEntityType;
    entity_id: string;
}

export enum QueryParams {
    LEVEL = 'level',
    TYPE = 'type',
    CATEGORY = 'category'
}
export type EventQueryParams = DefaultQueryParams & QueryParams;
export type EventListResponse = PaginatedResponse<Event>;
export type EventEntitiesListResponse = PaginatedResponse<EventEntity>;

export interface IEventsFilter {
    type?: EventEntityType;
    id?: string;
    from?: string;
    to?: string;
}
